import React, { useState } from "react";
import { AlertCircle } from "lucide-react";
import {
  maskCPF,
  maskCNPJ,
  maskPhone,
  maskCEP,
  onlyDigits,
  isValidCPF,
  isValidCNPJ,
} from "../utils/validators";

const WHATSAPP_NUMBER = "5511969887637"; // DDI(55) + DDD(11) + Numero

const initialForm = {
  tipo: "",
  nome: "",
  whatsapp: "",
  cpf: "",
  cnpj: "",
  comercio: "",
  banco: "",
  cep: "",
  cidade: "",
  endereco: "",
  metros: "",
  horario: "",
  espaco: "",
  equipe: "",
  observacoes: "",
  faturamento: "",
  renda_liquida: "",
  consent: false,
};

export default function RegisterForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [cepLoading, setCepLoading] = useState(false);
  const [cepMsg, setCepMsg] = useState("");
  const [sent, setSent] = useState(false);

  const setField = (name, value) => {
    let v = value;
    if (name === "cpf") v = maskCPF(value);
    if (name === "cnpj") v = maskCNPJ(value);
    if (name === "whatsapp") v = maskPhone(value);
    if (name === "cep") v = maskCEP(value);
    setForm((p) => ({ ...p, [name]: v }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const buscarCEP = async (cepValue) => {
    const cep = onlyDigits(cepValue);
    setCepMsg("");
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await res.json();
      if (data.erro) {
        setCepMsg("CEP não encontrado.");
        return;
      }
      const cidade =
        data.localidade && data.uf ? `${data.localidade} - ${data.uf}` : "";
      const enderecoParts = [data.logradouro, data.bairro].filter(Boolean);
      setForm((p) => ({
        ...p,
        cidade: cidade || p.cidade,
        endereco: enderecoParts.length ? enderecoParts.join(", ") : p.endereco,
      }));
      setCepMsg("Endereço preenchido automaticamente.");
    } catch (e) {
      setCepMsg("Não foi possível buscar o CEP.");
    } finally {
      setCepLoading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.tipo) e.tipo = "Selecione uma opção.";
    if (!form.nome.trim()) e.nome = "Informe o nome do responsável.";
    if (!form.whatsapp.trim()) e.whatsapp = "Informe o WhatsApp.";

    // CPF obrigatório e válido
    if (!onlyDigits(form.cpf)) e.cpf = "CPF é obrigatório.";
    else if (!isValidCPF(form.cpf)) e.cpf = "CPF inválido";

    // CNPJ opcional, válido se preenchido
    if (onlyDigits(form.cnpj) && !isValidCNPJ(form.cnpj)) e.cnpj = "CNPJ inválido";

    if (!form.comercio.trim()) e.comercio = "Informe o nome do comércio.";
    if (!form.banco.trim()) e.banco = "Informe a instituição bancária.";
    if (!form.cep.trim()) e.cep = "Informe o CEP.";
    if (!form.cidade.trim()) e.cidade = "Informe a cidade e estado.";
    if (!form.endereco.trim()) e.endereco = "Informe o endereço completo.";
    if (!String(form.metros).trim()) e.metros = "Informe os metros disponíveis.";
    if (!form.horario.trim()) e.horario = "Informe o horário.";
    if (!form.espaco) e.espaco = "Selecione uma opção.";
    if (!form.equipe) e.equipe = "Selecione uma opção.";
    if (!form.faturamento) e.faturamento = "Selecione uma faixa.";
    if (!form.renda_liquida) e.renda_liquida = "Selecione uma faixa.";
    if (!form.consent) e.consent = "É necessário confirmar a declaração.";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) {
      const firstErr = document.querySelector(".invalid, .consent");
      if (firstErr) firstErr.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    // Dispara evento SubmitApplication no Meta Pixel (Facebook Ads)
    if (typeof window !== "undefined" && typeof window.fbq !== "undefined") {
      window.fbq("track", "SubmitApplication", {
        content_name: "Cadastro Ponto de Coleta",
        status: true,
      });
    }

    const linhas = [
      "*NOVO CADASTRO - PONTO DE COLETA*",
      "",
      `*Tipo:* ${form.tipo}`,
      `*Nome do responsável:* ${form.nome}`,
      `*WhatsApp:* ${form.whatsapp}`,
      `*CPF:* ${form.cpf}`,
    ];
    if (onlyDigits(form.cnpj)) linhas.push(`*CNPJ:* ${form.cnpj}`);
    linhas.push(`*Comércio/Ponto:* ${form.comercio}`);
    linhas.push(`*Instituição bancária:* ${form.banco}`);
    linhas.push(`*CEP:* ${form.cep}`);
    linhas.push(`*Cidade/Estado:* ${form.cidade}`);
    linhas.push(`*Endereço:* ${form.endereco}`);
    linhas.push(`*Metros disponíveis:* ${form.metros}`);
    linhas.push(`*Horário:* ${form.horario}`);
    linhas.push(`*Espaço coberto e seguro:* ${form.espaco}`);
    linhas.push(`*Possui equipe:* ${form.equipe}`);
    if (form.observacoes.trim()) linhas.push(`*Observações:* ${form.observacoes}`);
    linhas.push(`*Faturamento mensal:* ${form.faturamento}`);
    linhas.push(`*Renda líquida mensal:* ${form.renda_liquida}`);

    const mensagem = encodeURIComponent(linhas.join("\n"));
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${mensagem}`;

    setSent(true);
    setTimeout(() => {
      window.open(url, "_blank");
      setSent(false);
    }, 400);
  };

  const cls = (name) => (errors[name] ? "invalid" : "");
  const Err = ({ name }) =>
    errors[name] ? (
      <span className="field-error">
        <AlertCircle size={13} /> {errors[name]}
      </span>
    ) : null;

  return (
    <form className="register-form" id="register-form" onSubmit={handleSubmit} noValidate>
      <div className="form-question">
        <span>Você está se cadastrando como:</span>
        <label>
          <input
            type="radio"
            name="tipo"
            value="Pessoa Física"
            checked={form.tipo === "Pessoa Física"}
            onChange={(e) => setField("tipo", e.target.value)}
          />{" "}
          Pessoa física
        </label>
        <label>
          <input
            type="radio"
            name="tipo"
            value="Pessoa Jurídica"
            checked={form.tipo === "Pessoa Jurídica"}
            onChange={(e) => setField("tipo", e.target.value)}
          />{" "}
          Pessoa jurídica
        </label>
      </div>
      <Err name="tipo" />

      <div className="form-grid">
        <label>
          Nome do responsável
          <input
            className={cls("nome")}
            type="text"
            placeholder="Seu nome completo"
            value={form.nome}
            onChange={(e) => setField("nome", e.target.value)}
          />
          <Err name="nome" />
        </label>
        <label>
          WhatsApp para contato
          <input
            className={cls("whatsapp")}
            type="tel"
            placeholder="(00) 00000-0000"
            value={form.whatsapp}
            onChange={(e) => setField("whatsapp", e.target.value)}
          />
          <Err name="whatsapp" />
        </label>
      </div>

      <div className="form-grid">
        <label>
          CPF do responsável
          <input
            className={cls("cpf")}
            type="text"
            inputMode="numeric"
            placeholder="Ex.: 000.000.000-00"
            value={form.cpf}
            onChange={(e) => setField("cpf", e.target.value)}
          />
          <Err name="cpf" />
        </label>
        <label>
          CNPJ da empresa <small>(se houver)</small>
          <input
            className={cls("cnpj")}
            type="text"
            inputMode="numeric"
            placeholder="Ex.: 00.000.000/0000-00"
            value={form.cnpj}
            onChange={(e) => setField("cnpj", e.target.value)}
          />
          <Err name="cnpj" />
        </label>
      </div>

      <label>
        Nome do comércio ou ponto de coleta
        <input
          className={cls("comercio")}
          type="text"
          placeholder="Como o local é conhecido"
          value={form.comercio}
          onChange={(e) => setField("comercio", e.target.value)}
        />
        <Err name="comercio" />
      </label>

      <label>
        Instituição bancária para receber os repasses
        <input
          className={cls("banco")}
          type="text"
          placeholder="Ex.: Bradesco, Santander, Banco do Brasil..."
          value={form.banco}
          onChange={(e) => setField("banco", e.target.value)}
        />
        <Err name="banco" />
      </label>

      <div className="form-grid">
        <label>
          CEP
          <input
            className={cls("cep")}
            type="text"
            inputMode="numeric"
            placeholder="00000-000"
            value={form.cep}
            onChange={(e) => setField("cep", e.target.value)}
            onBlur={(e) => buscarCEP(e.target.value)}
          />
          <Err name="cep" />
        </label>
        <label>
          Cidade e estado
          <input
            className={cls("cidade")}
            type="text"
            placeholder="Ex.: São Paulo - SP"
            value={form.cidade}
            onChange={(e) => setField("cidade", e.target.value)}
          />
          <Err name="cidade" />
        </label>
      </div>
      {(cepLoading || cepMsg) && (
        <p className="cep-hint">{cepLoading ? "Buscando CEP..." : cepMsg}</p>
      )}

      <label>
        Endereço completo do local
        <input
          className={cls("endereco")}
          type="text"
          placeholder="Rua, número, complemento e bairro"
          value={form.endereco}
          onChange={(e) => setField("endereco", e.target.value)}
        />
        <Err name="endereco" />
      </label>

      <div className="form-grid">
        <label>
          Quantos metros estão disponíveis?
          <input
            className={cls("metros")}
            type="number"
            min="1"
            placeholder="Ex.: 12"
            value={form.metros}
            onChange={(e) => setField("metros", e.target.value)}
          />
          <Err name="metros" />
        </label>
        <label>
          Horário de funcionamento
          <input
            className={cls("horario")}
            type="text"
            placeholder="Ex.: Seg. a sex., 8h às 18h"
            value={form.horario}
            onChange={(e) => setField("horario", e.target.value)}
          />
          <Err name="horario" />
        </label>
      </div>

      <div className="form-question">
        <span>O local possui espaço coberto e seguro?</span>
        <label>
          <input
            type="radio"
            name="espaco"
            value="Sim"
            checked={form.espaco === "Sim"}
            onChange={(e) => setField("espaco", e.target.value)}
          />{" "}
          Sim
        </label>
        <label>
          <input
            type="radio"
            name="espaco"
            value="Não"
            checked={form.espaco === "Não"}
            onChange={(e) => setField("espaco", e.target.value)}
          />{" "}
          Não
        </label>
      </div>
      <Err name="espaco" />

      <div className="form-question">
        <span>Você possui equipe para receber e entregar os pacotes?</span>
        <label>
          <input
            type="radio"
            name="equipe"
            value="Sim"
            checked={form.equipe === "Sim"}
            onChange={(e) => setField("equipe", e.target.value)}
          />{" "}
          Sim
        </label>
        <label>
          <input
            type="radio"
            name="equipe"
            value="Não"
            checked={form.equipe === "Não"}
            onChange={(e) => setField("equipe", e.target.value)}
          />{" "}
          Não
        </label>
      </div>
      <Err name="equipe" />

      <label>
        Observações sobre o local <small>(opcional)</small>
        <textarea
          rows={3}
          placeholder="Conte algo importante sobre o seu ponto de coleta"
          value={form.observacoes}
          onChange={(e) => setField("observacoes", e.target.value)}
        />
      </label>

      <label>
        Faturamento mensal do comércio
        <select
          className={cls("faturamento")}
          value={form.faturamento}
          onChange={(e) => setField("faturamento", e.target.value)}
        >
          <option value="">Selecione uma faixa</option>
          <option>Até R$ 5.000</option>
          <option>De R$ 5.001 a R$ 10.000</option>
          <option>De R$ 10.001 a R$ 30.000</option>
          <option>De R$ 30.001 a R$ 60.000</option>
          <option>Acima de R$ 60.000</option>
        </select>
        <Err name="faturamento" />
      </label>

      <label>
        Renda líquida mensal
        <select
          className={cls("renda_liquida")}
          value={form.renda_liquida}
          onChange={(e) => setField("renda_liquida", e.target.value)}
        >
          <option value="">Selecione uma faixa</option>
          <option>Até R$ 2.000</option>
          <option>De R$ 2.001 a R$ 5.000</option>
          <option>De R$ 5.001 a R$ 10.000</option>
          <option>De R$ 10.001 a R$ 20.000</option>
          <option>Acima de R$ 20.000</option>
        </select>
        <Err name="renda_liquida" />
      </label>

      <label className="consent">
        <input
          type="checkbox"
          checked={form.consent}
          onChange={(e) => setField("consent", e.target.checked)}
        />{" "}
        Declaro que as informações prestadas são verdadeiras.
      </label>
      <Err name="consent" />

      <button className="submit-button" type="submit" disabled={sent}>
        {sent ? "Redirecionando ao WhatsApp..." : "Enviar cadastro ↗"}
      </button>
      <p className="form-security">
        Seus dados serão usados somente para esta análise.
      </p>
    </form>
  );
}
